import { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import toast from 'react-hot-toast';

import { Countdown } from '@/components/competition/Countdown';
import { useCompetitionCompletedGuard } from '@/hooks/useCompetitionCompletedGuard';
import { endCompetitionSession } from '@/hooks/useCompetitionSession';
import { PublicFooter } from '@/components/layout/Footer';
import { PublicHeader } from '@/components/layout/Header';
import { OrganizerLogoBar } from '@/components/layout/OrganizerLogoBar';
import { useCompetitionStore, useServerTime } from '@/store/competitionStore';
import { COMPETITION_PAGE_BG } from '@/components/layout/competitionBranding';

/** 5 minut od teraz – używane jako zastępczy startTime w trybie dev preview */
const DEV_PREVIEW_START = () => Date.now() + 5 * 60 * 1000;

function getRules(startStr: string, endStr: string) {
  return [
    `Test rozpoczyna się o godzinie ${startStr}.`,
    `Uczestnicy mają czas do godziny ${endStr}, aby uzupełnić test.`,
    'Pytania są jednokrotnego wyboru.',
    'Po przejściu do następnego pytania nie można cofać się do poprzedniego.',
    'Nie wolno opuszczać karty przeglądarki – zostanie to uznane za łamanie zasad i skutkować będzie dyskwalifikacją.',
  ];
}

function serverNowMs(serverTimeOffset: number): number {
  return Date.now() + serverTimeOffset;
}

function formatMs(ms: number): string {
  const totalSec = Math.floor(ms / 1000);
  const h = Math.floor(totalSec / 3600);
  const m = Math.floor((totalSec % 3600) / 60);
  const s = totalSec % 60;
  const pad = (n: number) => String(n).padStart(2, '0');
  return h > 0 ? `${pad(h)}:${pad(m)}:${pad(s)}` : `${pad(m)}:${pad(s)}`;
}

/**
 * Waiting room – displayed between /weryfikacja and /turniej.
 *
 * The student must manually click "Rozpocznij test" to enter the quiz.
 * This page stays visible until they do — both before and after the
 * scheduled start time (e.g. joining 59 minutes into the window).
 */
export const PoczekalniaPage = () => {
  const navigate = useNavigate();
  const { search } = useLocation();
  const { serverTimeOffset, startTime, deadlineMs } = useServerTime();
  const testStarted = useCompetitionStore((s) => s.testStarted);
  const sessionReady = useCompetitionStore((s) => s.sessionReady);
  const stage = useCompetitionStore((s) => s.stage);
  const markTestStarted = useCompetitionStore((s) => s.markTestStarted);

  const isDevPreview = import.meta.env.DEV && new URLSearchParams(search).has('preview');

  useCompetitionCompletedGuard({
    enabled: !isDevPreview && sessionReady,
  });
  const effectiveStartTime = isDevPreview ? DEV_PREVIEW_START() : startTime;
  const effectiveOffset = isDevPreview ? 0 : serverTimeOffset;
  const effectiveDeadline = isDevPreview
    ? effectiveStartTime + 60 * 60 * 1000
    : deadlineMs;

  const timeFormatter = new Intl.DateTimeFormat('pl-PL', {
    hour: '2-digit',
    minute: '2-digit',
    timeZone: 'Europe/Warsaw',
  });
  const startStr = timeFormatter.format(new Date(effectiveStartTime));
  const endStr = timeFormatter.format(new Date(effectiveDeadline));
  const rulesList = getRules(startStr, endStr);

  const [canStart, setCanStart] = useState(() =>
    serverNowMs(effectiveOffset) >= effectiveStartTime,
  );
  const [remainingMs, setRemainingMs] = useState(() =>
    Math.max(0, effectiveDeadline - serverNowMs(effectiveOffset)),
  );
  const deadlineHandledRef = useRef(false);

  // Already in the test — skip waiting room (e.g. manual URL navigation).
  useEffect(() => {
    if (testStarted) {
      navigate('/turniej', { replace: true });
    }
  }, [testStarted, navigate]);

  // Track whether the scheduled start has passed and how much test time remains.
  useEffect(() => {
    const update = () => {
      const now = serverNowMs(effectiveOffset);
      setCanStart(now >= effectiveStartTime);
      setRemainingMs(Math.max(0, effectiveDeadline - now));
    };

    update();
    const id = setInterval(update, 1_000);
    return () => clearInterval(id);
  }, [effectiveStartTime, effectiveDeadline, effectiveOffset]);

  // When deadline passes while user is in the waiting room, end session.
  useEffect(() => {
    if (isDevPreview || effectiveDeadline === 0) return;
    if (remainingMs > 0 || deadlineHandledRef.current) return;

    deadlineHandledRef.current = true;
    toast.error('Czas na ukończenie testu minął.');
    void endCompetitionSession().then(() => {
      navigate('/', { replace: true });
    });
  }, [remainingMs, effectiveDeadline, isDevPreview, navigate]);

  const handleCountdownComplete = useCallback(() => {
    setCanStart(true);
  }, []);

  const handleStartClick = useCallback(() => {
    if (!canStart) return;
    markTestStarted();
    navigate('/turniej', { replace: true });
  }, [canStart, markTestStarted, navigate]);

  const beforeStart = !canStart;
  const testAvailable = canStart && remainingMs > 0;

  return (
    <div
      className="font-rounded min-h-dvh"
      style={{ background: COMPETITION_PAGE_BG }}
    >
      <PublicHeader />
      <OrganizerLogoBar />

      <main className="px-4 py-4 md:py-6">
        <div className="mx-auto flex w-full max-w-6xl flex-col gap-3 md:min-h-[32rem] md:max-h-[46rem] md:flex-row md:items-stretch md:gap-6">

          {/* Lewa kolumna: tytuł + countdown / start */}
          <div className="flex flex-col gap-5 md:min-h-0 md:w-1/2">
            <div className="rounded-3xl bg-white/90 px-8 py-6 text-center">
              <span
                className="inline-block rounded-full px-4 py-0.5 text-xs font-semibold uppercase tracking-widest"
                style={{ backgroundColor: 'rgba(76,197,240,0.15)', color: '#1aaedd' }}
              >
                Ogólnopolski turniej dla szkół technicznych
              </span>
              <h1 className="mt-2 text-xl font-bold tracking-tight text-gray-800 sm:text-2xl">
                Turniej Przetwórstwo Mleczarskie
              </h1>
              <p className="mt-1 text-xs text-gray-500">
                {beforeStart
                  ? 'Przygotuj się – turniej zaraz się rozpocznie'
                  : 'Zapoznaj się z zasadami i rozpocznij test, gdy będziesz gotowy/a'}
              </p>
            </div>

            <div className="flex flex-1 flex-col items-center justify-center rounded-3xl bg-white/90 px-8 py-7 text-center">
              {beforeStart ? (
                <>
                  <Countdown
                    startTime={effectiveStartTime}
                    serverTimeOffset={effectiveOffset}
                    onComplete={handleCountdownComplete}
                  />
                  <p className="mt-3 text-xs text-gray-400">
                    Gdy odliczanie dobiegnie końca, przycisk poniżej się aktywuje.
                  </p>
                </>
              ) : testAvailable ? (
                <>
                  <p className="text-sm font-medium uppercase tracking-widest text-gray-400">
                    Pozostały czas na test
                  </p>
                  <p
                    className="mt-2 font-mono text-5xl font-bold tabular-nums text-gray-800 sm:text-6xl"
                    aria-live="polite"
                  >
                    {formatMs(remainingMs)}
                  </p>
                  <p className="mt-3 text-xs text-gray-400">
                    Test jest dostępny. Przeczytaj zasady po prawej stronie, a następnie kliknij przycisk.
                  </p>
                </>
              ) : (
                <p className="text-sm font-medium text-gray-500">
                  Czas na ukończenie testu minął.
                </p>
              )}

              <button
                type="button"
                onClick={handleStartClick}
                disabled={!testAvailable}
                className="mt-4 w-full max-w-xs rounded-xl bg-competition-accent px-6 py-3 text-sm font-semibold text-white transition hover:bg-competition-accent-dark disabled:cursor-not-allowed disabled:opacity-60 focus:outline-none focus:ring-2 focus:ring-competition-accent focus:ring-offset-2"
              >
                Rozpocznij test
              </button>
              {testAvailable || beforeStart ? (
                <div
                  className={`mt-3 flex max-w-xs items-start gap-2 rounded-xl border px-3 py-2.5 text-left transition-opacity ${
                    testAvailable
                      ? 'border-competition-accent/25 bg-competition-accent/8'
                      : 'border-gray-200/80 bg-gray-50/80 opacity-80'
                  }`}
                >
                  <span
                    className="mt-0.5 flex h-4 w-4 shrink-0 items-center justify-center rounded-full text-[9px] font-bold text-white"
                    style={{ backgroundColor: testAvailable ? '#4cc5f0' : '#cbd5e1' }}
                    aria-hidden
                  >
                    i
                  </span>
                  <p
                    className={`text-[11px] leading-relaxed ${
                      testAvailable ? 'font-medium text-gray-600' : 'text-gray-400'
                    }`}
                  >
                    Klikając przycisk „Rozpocznij test”, potwierdzam zapoznanie się z zasadami
                    turnieju.
                  </p>
                </div>
              ) : (
                <p className="mt-2 text-[11px] font-medium text-gray-400">
                  Skontaktuj się z organizatorem.
                </p>
              )}
            </div>
          </div>

          {/* Prawa kolumna: zasady */}
          <div className="flex flex-col rounded-3xl bg-white/90 px-8 py-6 md:min-h-0 md:w-1/2">
            <h2
              className="mb-3 text-xs font-bold uppercase tracking-widest"
              style={{ color: '#1aaedd' }}
            >
              Zasady turnieju
            </h2>
            <ol className="flex flex-1 flex-col justify-around gap-2">
              {rulesList.map((rule, i) => (
                <li key={i} className="flex items-start gap-3">
                  <span
                    className="flex h-5 w-5 shrink-0 items-center justify-center rounded-full text-[10px] font-bold text-white"
                    style={{ backgroundColor: '#4cc5f0' }}
                  >
                    {i + 1}
                  </span>
                  <span className="text-xs leading-relaxed text-gray-600 sm:text-sm">{rule}</span>
                </li>
              ))}
            </ol>
            <div className="mt-4 rounded-xl border border-blue-100 bg-blue-50/50 p-3 text-center">
              <p className="text-[11px] leading-relaxed text-gray-600 md:text-xs">
                {stage === 2 ? (
                  <>
                    Wynik końcowy to suma punktów z testu teoretycznego i egzaminu praktycznego. <br />
                    W przypadku remisu, rozstrzygający będzie{' '}
                    <strong className="font-semibold text-gray-700">łączny czas rozwiązania testu</strong>.
                  </>
                ) : (
                  <>
                    Pamiętaj, że o awansie do kolejnego etapu decyduje w głównej mierze zdobycz punktowa. <br />
                    Jeżeli kilku uczestników uzyska ten sam wynik, rozstrzygający okaże się{' '}
                    <strong className="font-semibold text-gray-700">czas rozwiązania testu</strong>.
                  </>
                )}
              </p>
            </div>
            <p className="mt-3 text-center text-sm font-semibold" style={{ color: '#1aaedd' }}>
              Powodzenia!
            </p>
          </div>

        </div>
      </main>

      <PublicFooter />
    </div>
  );
};
